// An organisation's online payments, as head office sets them up (F-PAY-05, F-HQ-13): the commission the
// platform takes from each online payment, and the payout details the payment provider pays the café's share
// to. It sits on the organisation's page for a role holding `hq:payments`; changing the commission also
// needs `hq:settings`, because it is an organisation setting and an organisation can see it but never change it.
//
// The full account number and PAN are sent to the payment provider and are not kept: the api hands back the
// last four of each, so correcting a payee means typing those two again.

import type { Payee, SavePayeeRequest } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, type BadgeTone, Button, Dialog, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { fieldErrors, problemDetail, problemStatus } from '../../api/problems';
import { bpsOf, percentOf } from '../../settings/tax-categories';
import { Loading } from '../../shell/layouts';
import { LoadFailure } from '../../shell/LoadFailure';
import { usePlatformAccessHere } from '../access';
import { HQ_SETTINGS } from '../sections';
import { useOrganizationPayments, useRefreshPayee, useSaveCommission, useSavePayee } from './queries';

/** The most the platform can take, in basis points: the setting's own ceiling. */
const MAX_COMMISSION_BPS = 5000;

const BUSINESS_TYPES = ['individual', 'proprietorship', 'partnership', 'private_limited', 'llp'] as const;

const STATUS_TONE: Readonly<Record<Payee['status'], BadgeTone>> = {
  pending: 'info',
  action_required: 'warn',
  active: 'ok',
  rejected: 'danger',
  suspended: 'danger',
};

// The contract's own patterns (`packages/contracts/src/routes/hq-payments.ts`); the server checks them again.
const PAN = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;
const GSTIN = /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z][Zz][0-9A-Za-z]$/;
const IFSC = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;
const ACCOUNT_NUMBER = /^[0-9]{9,18}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE = /^(\+?91)?[0-9]{10}$/;

export function PaymentsPanel({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('hq');
  const payments = useOrganizationPayments(organizationId);

  let body: ReactNode;
  if (payments.data === undefined) {
    body = payments.isError ? (
      <LoadFailure error={payments.error} message={t('organizations.payments.unavailable')} onRetry={() => void payments.refetch()} />
    ) : (
      <Loading />
    );
  } else if (payments.data.provider === null) {
    body = <p className="console-muted">{t('organizations.payments.notSetUp')}</p>;
  } else {
    body = (
      <div className="console-hq-payments">
        <CommissionRow organizationId={organizationId} commissionBps={payments.data.commissionBps} />
        {payments.data.settlement === 'split' ? <PayoutsBlock organizationId={organizationId} payee={payments.data.payee} /> : null}
      </div>
    );
  }

  return (
    <section className="console-hq-panel" aria-labelledby="console-hq-payments">
      <h2 id="console-hq-payments">{t('organizations.payments.title')}</h2>
      {body}
    </section>
  );
}

function CommissionRow({ organizationId, commissionBps }: { readonly organizationId: string; readonly commissionBps: number }) {
  const { t } = useTranslation('hq');
  const access = usePlatformAccessHere();
  const { show } = useToast();
  const save = useSaveCommission(organizationId);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const canEdit = access.permissions.has(HQ_SETTINGS);

  function begin(): void {
    setText(percentOf(commissionBps));
    setError(undefined);
    setEditing(true);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    const bps = bpsOf(text);
    if (bps === null || bps > MAX_COMMISSION_BPS) {
      setError(t('organizations.payments.commission.invalid'));
      return;
    }
    setError(undefined);
    save.mutate(bps, {
      onSuccess: () => {
        setEditing(false);
        show({ tone: 'ok', title: t('organizations.payments.commission.saved') });
      },
      onError: () => setError(t('organizations.payments.commission.failed')),
    });
  }

  return (
    <div className="console-hq-payments__block">
      <h3>{t('organizations.payments.commission.title')}</h3>
      {editing ? (
        <form className="console-form" noValidate aria-label={t('organizations.payments.commission.title')} onSubmit={submit}>
          <TextField
            label={t('organizations.payments.commission.label')}
            description={t('organizations.payments.commission.hint')}
            inputMode="decimal"
            value={text}
            error={error}
            onChange={(event) => setText(event.target.value)}
          />
          <div className="console-form__actions">
            <Button variant="ghost" onClick={() => setEditing(false)}>
              {t('organizations.payments.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={save.isPending}>
              {t('organizations.payments.commission.save')}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <p>
            <strong>{t('organizations.payments.commission.value', { percent: percentOf(commissionBps) })}</strong>
          </p>
          <p className="console-muted">{t('organizations.payments.commission.explain')}</p>
          {canEdit ? (
            <div className="console-form__actions">
              <Button variant="secondary" onClick={begin}>
                {t('organizations.payments.commission.edit')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function PayoutsBlock({ organizationId, payee }: { readonly organizationId: string; readonly payee: Payee | null }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const { show } = useToast();
  const refresh = useRefreshPayee(organizationId);
  const [open, setOpen] = useState(false);
  const [refreshError, setRefreshError] = useState<string | undefined>(undefined);

  function check(): void {
    setRefreshError(undefined);
    refresh.mutate(undefined, {
      onSuccess: () => show({ tone: 'ok', title: t('organizations.payments.payouts.checked') }),
      onError: (error) =>
        setRefreshError(problemStatus(error) === 503 ? t('organizations.payments.payouts.providerDown') : t('organizations.payments.payouts.checkFailed')),
    });
  }

  return (
    <div className="console-hq-payments__block">
      <h3>{t('organizations.payments.payouts.title')}</h3>
      {payee === null ? (
        <>
          <p>{t('organizations.payments.payouts.none')}</p>
          <div className="console-form__actions">
            <Button variant="primary" onClick={() => setOpen(true)}>
              {t('organizations.payments.payouts.setUp')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p>
            <Badge tone={STATUS_TONE[payee.status]} dot>
              {t(`organizations.payments.status.${payee.status}`)}
            </Badge>
          </p>
          <dl className="console-hq-terms">
            <dt>{t('organizations.payments.payouts.business')}</dt>
            <dd>{payee.businessName}</dd>
            <dt>{t('organizations.payments.payouts.bank')}</dt>
            <dd>{t('organizations.payments.payouts.bankValue', { last4: payee.bankAccountLast4, ifsc: payee.bankIfsc })}</dd>
            <dt>{t('organizations.payments.payouts.pan')}</dt>
            <dd>{t('organizations.payments.payouts.panValue', { last4: payee.panLast4 })}</dd>
            {payee.gstin === null ? null : (
              <>
                <dt>{t('organizations.payments.payouts.gstin')}</dt>
                <dd>
                  <span className="console-code">{payee.gstin}</span>
                </dd>
              </>
            )}
            <dt>{t('organizations.payments.payouts.contact')}</dt>
            <dd>{t('organizations.payments.payouts.contactValue', { name: payee.contactName, email: payee.contactEmail, phone: payee.contactPhone })}</dd>
          </dl>
          <p className="console-muted">
            {payee.statusCheckedAt === null
              ? t('organizations.payments.payouts.neverChecked')
              : t('organizations.payments.payouts.checkedAt', { when: format.relative(payee.statusCheckedAt) })}
          </p>
          {refreshError === undefined ? null : (
            <div className="console-alert" role="alert">
              <p>{refreshError}</p>
            </div>
          )}
          <div className="console-form__actions">
            <Button variant="secondary" busy={refresh.isPending} onClick={check}>
              {t('organizations.payments.payouts.check')}
            </Button>
            <Button variant="secondary" onClick={() => setOpen(true)}>
              {t('organizations.payments.payouts.update')}
            </Button>
          </div>
        </>
      )}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        size="md"
        title={payee === null ? t('organizations.payments.dialog.setUpTitle') : t('organizations.payments.dialog.updateTitle')}
        description={t('organizations.payments.dialog.intro')}
      >
        {open ? <PayeeForm organizationId={organizationId} payee={payee} onClose={() => setOpen(false)} /> : null}
      </Dialog>
    </div>
  );
}

interface Values {
  businessName: string;
  businessType: string;
  pan: string;
  gstin: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  bankAccountHolder: string;
  bankAccountNumber: string;
  bankAccountNumberAgain: string;
  bankIfsc: string;
}

type Errors = Partial<Record<keyof Values, string>>;

const compact = (value: string): string => value.replace(/[\s-]+/g, '');

/** PAN and the account number are masked by the api, so they always start empty. */
function initialValues(payee: Payee | null): Values {
  return {
    businessName: payee?.businessName ?? '',
    businessType: payee?.businessType ?? 'proprietorship',
    pan: '',
    gstin: payee?.gstin ?? '',
    contactName: payee?.contactName ?? '',
    contactEmail: payee?.contactEmail ?? '',
    contactPhone: payee?.contactPhone ?? '',
    bankAccountHolder: payee?.bankAccountHolder ?? '',
    bankAccountNumber: '',
    bankAccountNumberAgain: '',
    bankIfsc: payee?.bankIfsc ?? '',
  };
}

function PayeeForm({ organizationId, payee, onClose }: { readonly organizationId: string; readonly payee: Payee | null; readonly onClose: () => void }) {
  const { t } = useTranslation('hq');
  const { show } = useToast();
  const save = useSavePayee(organizationId);
  const [values, setValues] = useState<Values>(() => initialValues(payee));
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const set = (name: keyof Values) => (event: { target: { value: string } }) => setValues((current) => ({ ...current, [name]: event.target.value }));

  function validate(): Errors {
    const required = t('organizations.payments.dialog.error.required');
    const found: Errors = {};
    if (values.businessName.trim().length < 3) {
      found.businessName = values.businessName.trim() === '' ? required : t('organizations.payments.dialog.error.businessName');
    }
    if (!PAN.test(values.pan.trim())) found.pan = values.pan.trim() === '' ? required : t('organizations.payments.dialog.error.pan');
    if (values.gstin.trim() !== '' && !GSTIN.test(values.gstin.trim())) found.gstin = t('organizations.payments.dialog.error.gstin');
    if (values.contactName.trim().length < 2) found.contactName = required;
    if (!EMAIL.test(values.contactEmail.trim())) {
      found.contactEmail = values.contactEmail.trim() === '' ? required : t('organizations.payments.dialog.error.email');
    }
    if (!MOBILE.test(compact(values.contactPhone))) {
      found.contactPhone = values.contactPhone.trim() === '' ? required : t('organizations.payments.dialog.error.phone');
    }
    if (values.bankAccountHolder.trim().length < 3) found.bankAccountHolder = required;
    if (!ACCOUNT_NUMBER.test(values.bankAccountNumber.trim())) {
      found.bankAccountNumber = values.bankAccountNumber.trim() === '' ? required : t('organizations.payments.dialog.error.account');
    }
    if (values.bankAccountNumberAgain.trim() !== values.bankAccountNumber.trim()) {
      found.bankAccountNumberAgain = t('organizations.payments.dialog.error.accountMatch');
    }
    if (!IFSC.test(values.bankIfsc.trim())) found.bankIfsc = values.bankIfsc.trim() === '' ? required : t('organizations.payments.dialog.error.ifsc');
    return found;
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const body: SavePayeeRequest = {
      businessName: values.businessName.trim(),
      businessType: values.businessType as SavePayeeRequest['businessType'],
      pan: values.pan.trim().toUpperCase(),
      ...(values.gstin.trim() === '' ? {} : { gstin: values.gstin.trim().toUpperCase() }),
      contactName: values.contactName.trim(),
      contactEmail: values.contactEmail.trim(),
      contactPhone: compact(values.contactPhone),
      bankAccountHolder: values.bankAccountHolder.trim(),
      bankAccountNumber: values.bankAccountNumber.trim(),
      bankIfsc: values.bankIfsc.trim().toUpperCase(),
    };
    save.mutate(body, {
      onSuccess: () => {
        onClose();
        show({ tone: 'ok', title: t('organizations.payments.dialog.saved') });
      },
      onError: (error) => {
        const status = problemStatus(error);
        const detail = problemDetail(error);
        const fields = fieldErrors(error).map((one) => one.message);
        if (status === 503) setFailure(t('organizations.payments.dialog.providerDown'));
        else if (status === 409) setFailure(detail ?? t('organizations.payments.dialog.unavailable'));
        else if (status === 400) {
          setFailure([detail, ...fields].filter((line): line is string => line !== undefined).join(' ') || t('organizations.payments.dialog.refused'));
        } else setFailure(t('organizations.payments.dialog.failed'));
      },
    });
  }

  const types = BUSINESS_TYPES.map((type) => ({ value: type, label: t(`organizations.payments.businessType.${type}`) }));

  return (
    <form className="console-form" noValidate aria-label={t('organizations.payments.dialog.formLabel')} onSubmit={submit}>
      {failure === undefined ? null : (
        <div className="console-alert" role="alert">
          <p>{failure}</p>
        </div>
      )}
      <TextField label={t('organizations.payments.dialog.businessName')} required value={values.businessName} error={errors.businessName} onChange={set('businessName')} />
      <SelectField
        label={t('organizations.payments.dialog.businessType')}
        required
        options={types}
        value={values.businessType}
        onValueChange={(value) => setValues((current) => ({ ...current, businessType: value }))}
      />
      <TextField
        label={t('organizations.payments.dialog.pan')}
        description={payee === null ? undefined : t('organizations.payments.dialog.retype')}
        required
        autoComplete="off"
        value={values.pan}
        error={errors.pan}
        onChange={set('pan')}
      />
      <TextField label={t('organizations.payments.dialog.gstin')} showOptional value={values.gstin} error={errors.gstin} onChange={set('gstin')} />
      <TextField label={t('organizations.payments.dialog.contactName')} required value={values.contactName} error={errors.contactName} onChange={set('contactName')} />
      <TextField
        label={t('organizations.payments.dialog.contactEmail')}
        required
        type="email"
        value={values.contactEmail}
        error={errors.contactEmail}
        onChange={set('contactEmail')}
      />
      <TextField
        label={t('organizations.payments.dialog.contactPhone')}
        required
        inputMode="tel"
        value={values.contactPhone}
        error={errors.contactPhone}
        onChange={set('contactPhone')}
      />
      <TextField label={t('organizations.payments.dialog.holder')} required value={values.bankAccountHolder} error={errors.bankAccountHolder} onChange={set('bankAccountHolder')} />
      <TextField
        label={t('organizations.payments.dialog.account')}
        description={payee === null ? undefined : t('organizations.payments.dialog.retype')}
        required
        inputMode="numeric"
        autoComplete="off"
        value={values.bankAccountNumber}
        error={errors.bankAccountNumber}
        onChange={set('bankAccountNumber')}
      />
      <TextField
        label={t('organizations.payments.dialog.accountAgain')}
        required
        inputMode="numeric"
        autoComplete="off"
        value={values.bankAccountNumberAgain}
        error={errors.bankAccountNumberAgain}
        onChange={set('bankAccountNumberAgain')}
      />
      <TextField label={t('organizations.payments.dialog.ifsc')} required value={values.bankIfsc} error={errors.bankIfsc} onChange={set('bankIfsc')} />
      <p className="console-muted">{t('organizations.payments.dialog.privacy')}</p>
      <div className="console-form__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('organizations.payments.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={save.isPending}>
          {t('organizations.payments.dialog.save')}
        </Button>
      </div>
    </form>
  );
}
